import { useEffect, useRef } from "react";

import { useActiveContextStore } from "../state/activeContextStore";
import type { ActiveContext } from "../types";

/**
 * useActiveContextScope — register an inline viewer's open entity with
 * the global active-context store while the viewer is mounted.
 *
 * Usage (every inline viewer that the user could ask the agent about)
 * ------------------------------------------------------------------
 *
 *     useActiveContextScope({
 *       kind: "calendar_event",
 *       id: event.event_id,
 *       title: event.title,
 *       summary: `${formatRange(start, end)} · ${event.location ?? ""}`,
 *       meta: { attendees: event.attendees.length },
 *     });
 *
 * The hook writes the context exactly once per identity change + a
 * clean unmount tear-down. The ``clearActive`` is id-guarded inside the
 * store so fast-navigation doesn't race-clear a newer viewer's entry.
 *
 * Pass ``null`` to opt out conditionally (e.g. the viewer is rendered
 * but the entity is still loading) — the store stays untouched.
 *
 * Identity vs. referential equality
 * ---------------------------------
 * The effect re-runs when the *fingerprint* of the context changes
 * (kind + id + title + summary) — NOT when the parent re-creates the
 * object literal with identical values. A ref carries the latest object
 * so the effect callback can write the freshest value without listing
 * ``ctx`` in the dep array (which would defeat the fingerprint guard).
 */
export function useActiveContextScope(ctx: ActiveContext | null): void {
  const setActive = useActiveContextStore((s) => s.setActive);
  const clearActive = useActiveContextStore((s) => s.clearActive);

  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const fingerprint = ctx
    ? `${ctx.kind}|${ctx.id}|${ctx.title}|${ctx.summary ?? ""}`
    : null;

  useEffect(() => {
    if (!fingerprint) return undefined;
    const current = ctxRef.current;
    if (!current) return undefined;
    setActive(current);
    const id = current.id;
    return () => clearActive(id);
  }, [fingerprint, setActive, clearActive]);
}
